import {HardwareItem} from "../../../../../../api/Hardware";
import {inches} from "../../const";
import {ElevationApp} from "../../ElevationApp";
import {MdDoorLite} from "../door/MdDoorLite";
import {MdHardwarePlacement} from "./MdHardware";
import {RuleLeaf} from "./MdHardwareRules";
import {hardwareFootprint} from "../../shared-2d/DoorHardware";
import {GlyphRect, hardwareGlyphFor} from "../../shared-2d/hardwareGlyphs";

// half-length of each arm of the marker drawn for an item with no picture of its own
// (see HardwareCrosshairThing).
export const MARKER_ARM = 2.5 * inches;

// an overlap below this (scene units = mm) is rounding, not a clash.
const OVERLAP_TOLERANCE = 0.01;

// the direction a lever reaches from its hub: toward the centre of the rect it sits in.
export function leverDirection(hubX: number, rect: GlyphRect): 1 | -1 {
    return hubX <= (rect.x0 + rect.x1) / 2 ? 1 : -1;
}

// the area a door placement covers on its leaf, in leaf coords (the leaf's top-left at the
// origin): the item's category glyph, else the lever / deadbolt of a primary placement,
// else the marker. Mirrors what HardwareCrosshairThing draws.
export function doorPlacementFootprint(leaf: RuleLeaf, placement: MdHardwarePlacement, item: HardwareItem | null): GlyphRect[] {
    const rect: GlyphRect = {x0: 0, y0: 0, x1: leaf.width, y1: leaf.height};
    const hub = {x: placement.x.get(), y: placement.y.get()};

    const glyph = item ? hardwareGlyphFor(item) : null;
    if(item && glyph && glyph.targets.includes("door")) {
        return glyph.footprint({item, hub, target: "door", rect, hingeSide: leaf.hingeSide, leaves: []});
    }

    if(placement.kind === "handle" || placement.kind === "lock") {
        return [hardwareFootprint(hub.x, hub.y, placement.kind, leverDirection(hub.x, rect))];
    }

    return [{x0: hub.x - MARKER_ARM, y0: hub.y - MARKER_ARM, x1: hub.x + MARKER_ARM, y1: hub.y + MARKER_ARM}];
}

// a lite's box in leaf coords.
export function liteRect(lite: MdDoorLite): GlyphRect {
    const x0 = lite.offsetLeft.get();
    const y0 = lite.offsetTop.get();
    return {x0, y0, x1: x0 + lite.width.get(), y1: y0 + lite.height.get()};
}

// the area two rects share, or null when they only touch or are apart.
export function overlapOf(a: GlyphRect, b: GlyphRect): GlyphRect | null {
    const r = {x0: Math.max(a.x0, b.x0), y0: Math.max(a.y0, b.y0), x1: Math.min(a.x1, b.x1), y1: Math.min(a.y1, b.y1)};
    if(r.x1 - r.x0 <= OVERLAP_TOLERANCE || r.y1 - r.y0 <= OVERLAP_TOLERANCE) return null;
    return r;
}

// a door placement whose footprint reaches into a lite on its leaf.
export type LiteOverlap = {
    placement: MdHardwarePlacement;
    item: HardwareItem;
    lite: MdDoorLite;
    // the shared area, in leaf coords.
    overlap: GlyphRect;
};

// the lites a door placement reaches into. Empty for a frame placement.
export function liteOverlapsOf(app: ElevationApp, placement: MdHardwarePlacement, item: HardwareItem): LiteOverlap[] {
    if(placement.target !== "door") return [];

    const leaf = app.hardware.leafFor(placement.side);
    const footprint = doorPlacementFootprint(leaf, placement, item);

    const out: LiteOverlap[] = [];
    for(const lite of app.doorSpec.litesFor(placement.side)) {
        // resolve the lite against the leaf's current size, as the views do before drawing it.
        lite.normalize(leaf.width, leaf.height);
        const box = liteRect(lite);

        const overlaps = footprint.map(r => overlapOf(r, box)).filter((r): r is GlyphRect => r !== null);
        if(overlaps.length === 0) continue;

        // report the whole area the item reaches into, not its first part.
        const overlap: GlyphRect = {
            x0: Math.min(...overlaps.map(r => r.x0)),
            y0: Math.min(...overlaps.map(r => r.y0)),
            x1: Math.max(...overlaps.map(r => r.x1)),
            y1: Math.max(...overlaps.map(r => r.y1)),
        };
        out.push({placement, item, lite, overlap});
    }
    return out;
}

// every door placement / lite overlap on the opening, in placement order.
export function liteOverlaps(app: ElevationApp): LiteOverlap[] {
    const items = app.hardwareGroup?.hardware ?? [];
    return app.hardware.list.flatMap(placement => {
        const item = items.find(h => h.product === placement.productId);
        return item ? liteOverlapsOf(app, placement, item) : [];
    });
}
